import { useMemo } from 'react';
import PaginationControls from '../common/PaginationControls';
import './PlanQueue.css';

export default function PlanQueue({
  plans,
  allPlans = plans,
  filters,
  onFilterChange,
  onSortChange,
  selectedPlanId,
  onSelectPlan,
  onOpenPreview,
  currentPage = 1,
  totalPages = 1,
  onPageChange = () => {},
}) {
  const sessions = useMemo(() => {
    const unique = new Set(allPlans.map(p => p.session).filter(s => s && s !== 'N/A'));
    return ['Toutes', ...Array.from(unique)];
  }, [allPlans]);

  const programmes = useMemo(() => {
    const unique = new Set(allPlans.map(p => p.programme).filter(p => p && p !== 'N/A'));
    return ['Tous', ...Array.from(unique)];
  }, [allPlans]);

  const modes = useMemo(() => {
    const unique = new Set(allPlans.map(p => p.mode_enseignement).filter(m => m && m !== 'N/A'));
    return ['Tous', ...Array.from(unique)];
  }, [allPlans]);

  const campus = useMemo(() => {
    const unique = new Set(allPlans.map(p => p.campus).filter(c => c && c !== 'N/A'));
    return ['Tous', ...Array.from(unique)];
  }, [allPlans]);

  const getSortIndicator = (column) => {
    if (filters.sortBy !== column) {
      return '↕';
    }
    return filters.sortDirection === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="plan-queue">
      <div className="queue-header">
        <h3>Queue de plans</h3>
        <div className="queue-filters">
          <select value={filters.status} onChange={(e) => onFilterChange('status', e.target.value)}>
            <option value="Tous">Tous les statuts</option>
            <option value="analyse">En analyse</option>
            <option value="validé">Validé</option>
            <option value="non validé">Non validé</option>
          </select>
          <input
            type="text"
            placeholder="Rechercher par enseignant..."
            value={filters.teacher}
            onChange={(e) => onFilterChange('teacher', e.target.value)}
            className="filter-search"
          />
          <input
            type="text"
            placeholder="Rechercher par cours..."
            value={filters.course}
            onChange={(e) => onFilterChange('course', e.target.value)}
            className="filter-search"
          />
          <select value={filters.session} onChange={(e) => onFilterChange('session', e.target.value)}>
            {sessions.map(s => <option key={s} value={s}>Session: {s}</option>)}
          </select>
          <select value={filters.programme} onChange={(e) => onFilterChange('programme', e.target.value)}>
            {programmes.map(p => <option key={p} value={p}>Programme: {p}</option>)}
          </select>
          <select value={filters.mode} onChange={(e) => onFilterChange('mode', e.target.value)}>
            {modes.map(m => <option key={m} value={m}>Mode: {m}</option>)}
          </select>
          <select value={filters.campus} onChange={(e) => onFilterChange('campus', e.target.value)}>
            {campus.map(c => <option key={c} value={c}>Campus: {c}</option>)}
          </select>
        </div>
      </div>

      <div className="table-wrapper">
        {allPlans.length === 0 ? (
          <p className="muted">Aucun plan assigné pour le moment.</p>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th><span className="sort-label">Titre du Plan <span className="sort-indicator" aria-hidden="true">{getSortIndicator('title')}</span></span></th>
                  <th><span className="sort-label">Cours <span className="sort-indicator" aria-hidden="true">{getSortIndicator('course')}</span></span></th>
                  <th><span className="sort-label">Enseignant <span className="sort-indicator" aria-hidden="true">{getSortIndicator('teacher')}</span></span></th>
                  <th><span className="sort-label">Statut <span className="sort-indicator" aria-hidden="true">{getSortIndicator('statusKey')}</span></span></th>
                  <th><span className="sort-label">Session <span className="sort-indicator" aria-hidden="true">{getSortIndicator('session')}</span></span></th>
                  <th><span className="sort-label">Programme <span className="sort-indicator" aria-hidden="true">{getSortIndicator('programme')}</span></span></th>
                  <th><span className="sort-label">Mode <span className="sort-indicator" aria-hidden="true">{getSortIndicator('mode_enseignement')}</span></span></th>
                  <th><span className="sort-label">Campus <span className="sort-indicator" aria-hidden="true">{getSortIndicator('campus')}</span></span></th>
                  <th><span className="sort-label">Date de soumission <span className="sort-indicator" aria-hidden="true">{getSortIndicator('submittedAt')}</span></span></th>
                  <th>Actions</th>
                </tr>
                <tr className="sort-row">
                  <th>
                    <select value={filters.sortBy === 'title' ? filters.sortDirection : ''} onChange={(e) => onSortChange?.('title', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">A-Z</option>
                      <option value="desc">Z-A</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'course' ? filters.sortDirection : ''} onChange={(e) => onSortChange?.('course', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">A-Z</option>
                      <option value="desc">Z-A</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'teacher' ? filters.sortDirection : ''} onChange={(e) => onSortChange?.('teacher', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">A-Z</option>
                      <option value="desc">Z-A</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'statusKey' ? filters.sortDirection : ''} onChange={(e) => onSortChange?.('statusKey', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">Priorité haute</option>
                      <option value="desc">Priorité basse</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'session' ? filters.sortDirection : ''} onChange={(e) => onSortChange?.('session', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">A-Z</option>
                      <option value="desc">Z-A</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'programme' ? filters.sortDirection : ''} onChange={(e) => onSortChange?.('programme', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">A-Z</option>
                      <option value="desc">Z-A</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'mode_enseignement' ? filters.sortDirection : ''} onChange={(e) => onSortChange?.('mode_enseignement', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">A-Z</option>
                      <option value="desc">Z-A</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'campus' ? filters.sortDirection : ''} onChange={(e) => onSortChange?.('campus', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="asc">A-Z</option>
                      <option value="desc">Z-A</option>
                    </select>
                  </th>
                  <th>
                    <select value={filters.sortBy === 'submittedAt' ? filters.sortDirection : ''} onChange={(e) => onSortChange?.('submittedAt', e.target.value)}>
                      <option value="">Tri</option>
                      <option value="desc">Plus récent</option>
                      <option value="asc">Plus ancien</option>
                    </select>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr
                    key={plan.id}
                    className={plan.id === selectedPlanId ? 'selected' : ''}
                    onClick={() => onSelectPlan?.(plan)}
                  >
                    <td className="title">{plan.title}</td>
                    <td>{plan.course}</td>
                    <td>{plan.teacher}</td>
                    <td>
                      <span className={`status ${plan.statusClass}`}>{plan.statusLabel}</span>
                    </td>
                    <td>{plan.session}</td>
                    <td>{plan.programme}</td>
                    <td>{plan.mode_enseignement}</td>
                    <td>{plan.campus}</td>
                    <td>{plan.submittedAt}</td>
                    <td>
                      {plan.pdfUrl && plan.pdfUrl !== '#' ? (
                        <button
                          className="view-btn"
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onOpenPreview?.(plan);
                          }}
                        >
                          Visualiser
                        </button>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          </>
        )}
      </div>
    </div>
  );
}
